export type Lang = 'en' | 'ar';

export const ADMIN_TRANSLATIONS: Record<Lang, Record<string, string>> = {
  en: {
    // ── Sidebar ──────────────────────────────────────────────────────────────
    'nav.main': 'Main',
    'nav.home': 'Home',
    'nav.categories': 'Categories',
    'nav.orders': 'Orders',
    'nav.active_orders': 'Active Orders',
    'nav.cancelled': 'Cancelled',
    'nav.archived': 'Archived',
    'nav.order_requests': 'Order Requests',
    'nav.catalog': 'Catalog',
    'nav.packages': 'Packages',
    'nav.blogs': 'Blogs',
    'nav.promo': 'Promo Codes',
    'nav.delivery': 'Delivery Regions',
    'nav.people': 'People',
    'nav.clients': 'Clients',
    'nav.admin_register': 'Admin Register',
    'nav.users': 'Users',

    // ── Header ───────────────────────────────────────────────────────────────
    'header.online_site': 'Online Site',
    'header.change_pw': 'Change Password',
    'header.logout': 'Logout',
    'header.notifications': 'Notifications',

    // ── Toast ─────────────────────────────────────────────────────────────────
    'toast.title_success': 'Done',
    'toast.title_error': 'Error',
    'toast.login_success': 'Logged in successfully',
    'toast.password_changed': 'Password changed successfully',
    'toast.password_reset': 'Password reset successfully',
    'toast.admin_created': 'New admin created successfully',
    'toast.category_added': 'Category added successfully',
    'toast.category_name_updated': 'Category name updated successfully',
    'toast.category_image_updated': 'Category image updated successfully',
    'toast.product_added': 'Product added successfully',
    'toast.product_deleted': 'Product deleted successfully',
    'toast.product_image_updated': 'Product image updated successfully',
    'toast.image_added': 'Image added successfully',
    'toast.variant_added': 'Variant added',
    'toast.variant_updated': 'Variant updated',
    'toast.variant_deleted': 'Variant deleted',
    'toast.review_updated': 'Review updated',
    'toast.package_added': 'Package added successfully',
    'toast.promo_added': 'Promo code added',
    'toast.promo_edited': 'Promo code updated',
    'toast.promo_deleted': 'Promo code deleted',
    'toast.blog_added': 'Blog added successfully',
    'toast.blog_updated': 'Blog updated',
    'toast.blog_deleted': 'Blog deleted',
    'toast.order_paid': 'Order marked as paid',
    'toast.order_delivered': 'Order marked as delivered',
    'toast.order_deleted': 'Order deleted',
    'toast.address_added': 'Address added successfully',
    'toast.status_updated': 'Status updated',
    'toast.user_updated': 'User updated successfully',
    'toast.user_deleted': 'User deleted',
    'toast.deleted_success': 'Deleted successfully',
    'toast.error_generic': 'Something went wrong, please try again',
    'toast.error_reload': 'Something went wrong, please reload the page',
    'toast.login_wrong_creds': 'Check your email or password',
    'toast.passwords_no_match': 'Passwords do not match',
    'toast.password_change_failed': 'Failed to change password',
    'toast.password_reset_failed': 'Failed to reset password',
    'toast.invalid_reset_link': 'Invalid or expired reset link',
    'toast.user_load_failed': 'Failed to load user',
    'toast.user_update_failed': 'Failed to update user',
    'toast.user_delete_failed': 'Failed to delete user',

    // ── Shared Buttons ────────────────────────────────────────────────────────
    'btn.add': 'Add',
    'btn.edit': 'Edit',
    'btn.delete': 'Delete',
    'btn.cancel': 'Cancel',
    'btn.save': 'Save',
    'btn.save_changes': 'Save Changes',
    'btn.continue': 'Continue',
    'btn.close': 'Close',
    'btn.retry': 'Retry',
    'btn.back': 'Back',
    'btn.back_signin': 'Back to Sign In',
    'btn.sign_in': 'Sign in',
    'btn.save_product': 'Save Product',
    'btn.add_category': 'Add Category',
    'btn.save_category': 'Save Category',
    'btn.add_product': 'Add Product',
    'btn.add_variant': 'Add Variant',
    'btn.save_variant': 'Save Variant',
    'btn.add_blog': 'Add Blog',
    'btn.save_blog': 'Save Blog',
    'btn.add_package': 'Add Package',
    'btn.save_package': 'Save Package',
    'btn.add_promo': 'Add Promo Code',
    'btn.create_code': 'Create Code',
    'btn.add_to_package': 'Add to Package',
    'btn.confirm_payment': 'Confirm Payment',
    'btn.confirm_delivery': 'Confirm Delivery',
    'btn.edit_order': 'Edit Order',
    'btn.cancel_order': 'Cancel Order',
    'btn.save_order': 'Save Order',
    'btn.keep_order': 'Keep Order',
    'btn.yes_cancel': 'Yes, Cancel',
    'btn.delete_user': 'Delete User',
    'btn.save_address': 'Save Address',
    'btn.change_address': 'Change Address',
    'btn.add_address': 'Add New Address',
    'btn.choose_category': 'Choose Category',
    'btn.choose_product': 'Choose Product',
    'btn.choose_package': 'Choose Package',
    'btn.create_admin': 'Create Admin Account',
    'btn.update_password': 'Update Password',
    'btn.send_reset': 'Send Reset Link',
    'btn.set_new_password': 'Set New Password',
    'btn.request_reset': 'Request Reset Link',
    'btn.view': 'View',

    // ── Shared Labels ─────────────────────────────────────────────────────────
    'lbl.uid': 'UID',
    'lbl.client': 'Client',
    'lbl.date': 'Date',
    'lbl.packages': 'Packages',
    'lbl.method': 'Method',
    'lbl.paid': 'Paid',
    'lbl.delivery': 'Delivery',
    'lbl.total': 'Total',
    'lbl.name': 'Name',
    'lbl.email': 'Email',
    'lbl.email_address': 'Email address',
    'lbl.phone': 'Phone',
    'lbl.phone_number': 'Phone Number',
    'lbl.username': 'Username',
    'lbl.password': 'Password',
    'lbl.current_password': 'Current Password',
    'lbl.new_password': 'New Password',
    'lbl.confirm_password': 'Confirm New Password',
    'lbl.price': 'Price',
    'lbl.quantity': 'Quantity',
    'lbl.discount': 'Discount',
    'lbl.total_price': 'Total Price',
    'lbl.description': 'Description',
    'lbl.products': 'Products',
    'lbl.category': 'Category',
    'lbl.actions': 'Actions',
    'lbl.content': 'Content',
    'lbl.steps': 'Steps',
    'lbl.published': 'Published',
    'lbl.in_stock': 'In Stock',
    'lbl.reason': 'Reason',
    'lbl.status': 'Status',
    'lbl.notes': 'Notes',
    'lbl.created': 'Created',
    'lbl.code': 'Code',
    'lbl.limit': 'Limit',
    'lbl.action': 'Action',
    'lbl.image': 'Image',
    'lbl.link': 'Link',
    'lbl.title': 'Title',
    'lbl.rating': 'Rating',
    'lbl.review': 'Review',
    'lbl.approved': 'Approved',
    'lbl.visible': 'Visible',
    'lbl.amount': 'Amount',
    'lbl.product': 'Product',
    'lbl.package': 'Package',
    'lbl.full_name': 'Full Name',
    'lbl.orders': 'Orders',
    'lbl.price_modifier': 'Price Modifier (LE)',
    'lbl.sku': 'SKU',
    'lbl.value': 'Value',
    'lbl.role': 'Role',
    'lbl.full_address': 'Full Address',
    'lbl.city': 'City',
    'lbl.governorate': 'Governorate',

    // ── Status Values ─────────────────────────────────────────────────────────
    'status.all': 'All',
    'status.paid': 'Paid',
    'status.unpaid': 'Unpaid',
    'status.delivered': 'Delivered',
    'status.pending': 'Pending',
    'status.undelivered': 'Undelivered',
    'status.cash': 'Cash',
    'status.vodafone': 'Vodafone',
    'status.yes': 'Yes',
    'status.no': 'No',
    'status.admin': 'Admin',
    'status.staff': 'Staff',
    'status.approved': 'Approved',
    'status.approve': 'Approve',
    'status.visible': 'Visible',
    'status.hidden': 'Hidden',
    'status.cash_on_delivery': 'Cash on Delivery',
    'status.vodafone_cash': 'Vodafone Cash',

    // ── Placeholders ──────────────────────────────────────────────────────────
    'ph.search_products': 'Search products...',
    'ph.search_clients': 'Search clients…',
    'ph.search_users': 'Search users…',
    'ph.search_phone': 'Search by phone...',
    'ph.search_product': 'Search by product...',
    'ph.category_name': 'Enter category name',
    'ph.product_name': 'Enter product name',
    'ph.price': '0.00',
    'ph.quantity': '0',
    'ph.description': 'Product description...',
    'ph.blog_title': 'Enter blog title...',
    'ph.blog_content': 'Write content...',
    'ph.blog_steps': 'Enter steps...',
    'ph.promo_code': 'e.g. SUMMER20',
    'ph.pack_name': 'Package name...',
    'ph.notes': 'Add notes...',
    'ph.phone_eg': '01XXXXXXXXX',
    'ph.email': 'admin@example.com',
    'ph.password': '••••••••',
    'ph.username_eg': 'e.g. admin_john',
    'ph.full_name': 'John Doe',
    'ph.phone_admin': '01xxxxxxxxx',

    // ── Page Headers ──────────────────────────────────────────────────────────
    'page.dashboard_eyebrow': 'Overview',
    'page.dashboard_title': 'Dashboard',
    'page.dashboard_subtitle': "Welcome back — here's what's happening today.",
    'page.categories_eyebrow': 'Catalog',
    'page.categories_title': 'Categories',
    'page.orders_title': 'All Orders',
    'page.orders_subtitle': 'View and manage all active customer orders',
    'page.archived_title': 'Archived Orders',
    'page.archived_subtitle': 'Completed orders — paid and delivered',
    'page.cancelled_title': 'Cancelled Orders',
    'page.cancelled_subtitle': 'Browse all cancelled customer orders',
    'page.order_req_title': 'Order Requests',
    'page.order_req_subtitle': 'Custom product requests submitted by clients',
    'page.clients_eyebrow': 'Users',
    'page.users_eyebrow': 'Admin',
    'page.users_title': 'Users',
    'page.users_subtitle': 'Manage staff accounts and admin permissions.',
    'page.change_pw_title': 'Change Password',
    'page.change_pw_subtitle': 'Update your admin account password',
    'page.blogs_eyebrow': 'Content',
    'page.blogs_title': 'Blogs',
    'page.packs_eyebrow': 'Inventory',
    'page.packs_title': 'Packages',
    'page.promo_eyebrow': 'Marketing',
    'page.promo_title': 'Promo Codes',
    'page.product_eyebrow': 'Product',
    'page.package_eyebrow': 'Package',
    'page.orders_count': 'orders',
    'page.users_count': 'users',

    // ── KPI / Dashboard ───────────────────────────────────────────────────────
    'kpi.total_clients': 'Total Clients',
    'kpi.total_revenue': 'Total Revenue',
    'kpi.total_orders': 'Total Orders',
    'kpi.pending_orders': 'Pending Orders',
    'kpi.order_status': 'Order Status',
    'kpi.total': 'total',
    'kpi.content_inventory': 'Content Inventory',
    'kpi.no_inventory': 'No inventory data yet',
    'kpi.orders_per_month': 'Orders per Month',
    'kpi.no_monthly_orders': 'No orders recorded in the last 12 months',
    'kpi.products': 'Products',
    'kpi.categories': 'Categories',
    'kpi.packages': 'Packages',
    'kpi.blogs': 'Blogs',
    'kpi.orders_unit': 'orders',
    'kpi.last_12_months': 'last 12 months',

    // ── Empty States ──────────────────────────────────────────────────────────
    'empty.categories': 'No Categories Yet',
    'empty.categories_sub': 'Add your first category to get started.',
    'empty.orders': 'No orders found',
    'empty.orders_sub': "Try adjusting your filters to find what you're looking for.",
    'empty.archived': 'No archived orders',
    'empty.cancelled': 'No cancelled orders',
    'empty.order_requests': 'No Order Requests',
    'empty.order_requests_sub': 'There are no custom order requests at this time.',
    'empty.delivery_regions': 'No delivery regions yet. Add one above.',
    'delivery.management': 'Shipping',
    'delivery.title': 'Delivery Regions',
    'delivery.add_region': 'Add Region',
    'delivery.regions': 'All Regions',
    'delivery.name_ar': 'Arabic Name',
    'delivery.price': 'Price (LE)',
    'ph.region_name': 'e.g. Cairo',
    'empty.clients': 'No Clients Found',
    'empty.clients_sub': 'No clients match your search.',
    'empty.users': 'No Users Found',
    'empty.users_sub': 'No users match your search.',
    'empty.users_error': 'Failed to Load Users',
    'empty.users_error_sub': 'Check the server is running and you are logged in as an admin.',
    'empty.blogs': 'No Blogs Found',
    'empty.blogs_sub': 'Create your first blog post to get started.',
    'empty.blog_products': 'No Products Linked',
    'empty.blog_products_sub': 'No products are linked to this blog post.',
    'empty.packages': 'No Packages Found',
    'empty.packages_sub': 'Create your first package to get started.',
    'empty.package_products': 'No Products',
    'empty.package_products_sub': 'This package has no products yet.',
    'empty.promo': 'No Promo Codes',
    'empty.promo_sub': 'Create your first promo code to offer discounts.',
    'empty.promo_orders': 'No Orders',
    'empty.promo_orders_sub': 'No orders have used this promo code yet.',
    'empty.client_orders': 'No Orders',
    'empty.client_orders_sub': 'This client has no orders yet.',
    'empty.variants': 'No variants added yet',
    'empty.reviews': 'No reviews yet',
    'empty.media': 'No media uploaded yet',
    'empty.no_description': 'No description available',
    'empty.products': 'No Products Found',
    'empty.products_sub': 'Try adjusting your search term.',
    'empty.no_cat_products': 'No products in this category',

    // ── Validation Errors ─────────────────────────────────────────────────────
    'err.required': 'Required',
    'err.email_required': 'Email address is required',
    'err.email_invalid': 'Email address is invalid',
    'err.username_required': 'Username is required',
    'err.name_required': 'Name is required',
    'err.phone_required': 'Phone number is required',
    'err.phone_length': 'Phone number must be exactly 11 digits',
    'err.password_required': 'Password is required',
    'err.password_min': 'Password must be at least 8 characters',
    'err.password_max': 'Password must be at most 128 characters',
    'err.password_mismatch': 'Passwords do not match.',
    'err.min8': 'Minimum 8 characters required.',
    'err.phone_valid': 'Please enter a valid 11-digit phone number',
    'err.cat_name_required': 'Category name is required',
    'err.invalid_reset_link': 'Invalid reset link. Please request a new one.',

    // ── Auth Pages ────────────────────────────────────────────────────────────
    'auth.brand_sub': 'Admin Dashboard',
    'auth.admin': 'Radwa Khallaf Admin',
    'auth.forgot_title': 'Forgot Password',
    'auth.forgot_hint': "Enter the email address for your admin account and we'll send you a reset link.",
    'auth.check_inbox': 'Check your inbox',
    'auth.check_inbox_sub': 'If that email is registered, a reset link has been sent. Check your spam folder too.',
    'auth.reset_title': 'Reset Password',
    'auth.reset_done': 'Password Reset!',
    'auth.reset_done_sub': 'Your password has been updated. Redirecting to sign in…',
    'auth.forgot_pw': 'Forgot password?',
    'auth.saving': 'Saving...',
    'auth.sending': 'Sending...',
    'auth.resetting': 'Resetting...',
    'auth.admin_registration': 'Admin Registration',
    'auth.grant_super': 'Grant Super Admin privileges',
    'auth.super_hint': 'Super admins can register other admins, view revenue, and manage all users.',

    // ── Product Page ──────────────────────────────────────────────────────────
    'prod.details': 'Product Details',
    'prod.media': 'Product Media',
    'prod.variants': 'Variants',
    'prod.reviews': 'Reviews',
    'prod.desc_en': 'Description (EN)',
    'prod.desc_ar': 'Description (AR)',
    'prod.images': 'Images',
    'prod.screenshots': 'Screenshots',
    'prod.videos': 'Videos',
    'prod.name_en': 'Name (EN)',
    'prod.name_ar_label': 'Name (AR)',
    'prod.add_images': 'Add Main Images',
    'prod.add_screenshots': 'Add Screenshots',
    'prod.add_video': 'Add Video',
    'prod.arabic_name': 'Arabic Name',
    'prod.arabic_desc': 'Arabic Description',
    'prod.step1': 'Step 1 of 2',
    'prod.step2': 'Step 2 of 2',
    'prod.step_details': 'Details',
    'prod.step_media': 'Media',
    'prod.saved_msg': 'Product saved. Add media below or skip to finish.',
    'prod.main_images': 'Main Images',
    'prod.product_video': 'Product Video',
    'prod.edit_title': 'Edit Product',

    // ── Category Page ─────────────────────────────────────────────────────────
    'cat.products_eyebrow': 'Category',
    'cat.edit_title': 'Edit Category',

    // ── Order Details ─────────────────────────────────────────────────────────
    'ord.information': 'Order Information',
    'ord.date_time': 'Date & Time',
    'ord.payment_method': 'Payment Method',
    'ord.delivery_address': 'Delivery Address',
    'ord.mobile': 'Mobile Number',
    'ord.delivery_fees': 'Delivery Fees',
    'ord.no_address': 'No address provided',
    'ord.current_products': 'Current Products',
    'ord.current_product': 'Current Product',
    'ord.remove_tip_pre': 'Set amount to',
    'ord.remove_tip_post': 'to remove',
    'ord.add_products': 'Add Products',
    'ord.add_packages': 'Add Packages',
    'ord.no_address_on_order': 'No address on this order',
    'ord.contact_phone': 'Contact Phone Number',
    'ord.cancel_title': 'Cancel Order?',
    'ord.cancel_sub': 'This action cannot be undone. The order will be permanently cancelled.',
    'ord.included_products': 'Included products',
    'ord.product_name': 'Product Name',
    'ord.price_unit': 'Price / Unit',
    'ord.subtotal': 'Subtotal',
    'ord.edit_title': 'Edit Order',

    // ── Blog ──────────────────────────────────────────────────────────────────
    'blog.title_label': 'Blog Title',
    'blog.content_label': 'Content',
    'blog.steps_label': 'Steps',
    'blog.linked_products': 'Linked Products',
    'blog.choose_product': 'Choose Product',
    'blog.images_label': 'Blog Images',
    'blog.video_label': 'Blog Video',
    'blog.edit': 'Edit Blog',
    'blog.delete': 'Delete Blog',
    'blog.add_images': 'Add Images',
    'blog.add_video': 'Add Video',

    // ── Package ───────────────────────────────────────────────────────────────
    'pack.category': 'Category',
    'pack.product_qty': 'Product Quantity',
    'pack.items': 'Package Items',
    'pack.pack_name': 'Pack Name',
    'pack.image_label': 'Package Image',
    'pack.delete': 'Delete Package',
    'pack.edit': 'Edit Package',
    'pack.included': 'Included products',
    'pack.qty_prefix': 'Qty:',
    'pack.quantities': 'Quantities',

    // ── Promo ─────────────────────────────────────────────────────────────────
    'promo.code_name': 'Promo Code Name',
    'promo.percentage_label': 'Discount Percentage',
    'promo.limit_label': 'Usage Limit',
    'promo.orders_using': 'Orders Using This Code',
    'promo.add_title': 'Add Promo Code',
    'promo.edit_title': 'Edit Promo Code',
    'promo.delete_title': 'Delete Promo Code',

    // ── User / Client ─────────────────────────────────────────────────────────
    'user.edit_info': 'Edit Info',
    'user.reset_pw': 'Reset Password',
    'user.admin_priv': 'Admin privileges',
    'user.admin_hint': 'Admins can access the Users management section.',
    'user.delete_confirm': 'Delete User?',
    'user.delete_msg': 'This action cannot be undone.',
    'user.min_pw': 'Min. 6 characters',

    // ── Confirm / Delete ──────────────────────────────────────────────────────
    'confirm.delete_category': 'Delete Category',
    'confirm.delete_product': 'Delete Product',
    'confirm.delete_blog': 'Delete Blog',
    'confirm.delete_package': 'Delete Package',
    'confirm.delete_promo': 'Delete Promo Code',
    'confirm.cannot_undo': 'This action cannot be undone.',
    'confirm.are_you_sure_delete': 'Are you sure you want to delete',

    // ── Filters ───────────────────────────────────────────────────────────────
    'filter.phone': 'Phone Number',
    'filter.product': 'Product Name',
    'filter.payment': 'Payment Status',
    'filter.delivery': 'Delivery Status',
  },

  ar: {
    // ── Sidebar ──────────────────────────────────────────────────────────────
    'nav.main': 'رئيسي',
    'nav.home': 'الرئيسية',
    'nav.categories': 'الفئات',
    'nav.orders': 'الطلبات',
    'nav.active_orders': 'الطلبات النشطة',
    'nav.cancelled': 'ملغاة',
    'nav.archived': 'مؤرشفة',
    'nav.order_requests': 'طلبات الطلب',
    'nav.catalog': 'الكتالوج',
    'nav.packages': 'الباقات',
    'nav.blogs': 'المدونة',
    'nav.promo': 'أكواد الخصم',
    'nav.delivery': 'مناطق التوصيل',
    'nav.people': 'الأشخاص',
    'nav.clients': 'العملاء',
    'nav.admin_register': 'تسجيل مشرف',
    'nav.users': 'المستخدمون',

    // ── Header ───────────────────────────────────────────────────────────────
    'header.online_site': 'الموقع الإلكتروني',
    'header.change_pw': 'تغيير كلمة المرور',
    'header.logout': 'تسجيل الخروج',
    'header.notifications': 'الإشعارات',

    // ── Toast ─────────────────────────────────────────────────────────────────
    'toast.title_success': 'تم',
    'toast.title_error': 'خطأ',
    'toast.login_success': 'تم تسجيل الدخول بنجاح',
    'toast.password_changed': 'تم تغيير كلمة المرور بنجاح',
    'toast.password_reset': 'تم إعادة تعيين كلمة المرور بنجاح',
    'toast.admin_created': 'تم إنشاء المشرف الجديد بنجاح',
    'toast.category_added': 'تمت إضافة الفئة بنجاح',
    'toast.category_name_updated': 'تم تحديث اسم الفئة بنجاح',
    'toast.category_image_updated': 'تم تحديث صورة الفئة بنجاح',
    'toast.product_added': 'تمت إضافة المنتج بنجاح',
    'toast.product_deleted': 'تم حذف المنتج بنجاح',
    'toast.product_image_updated': 'تم تحديث صورة المنتج بنجاح',
    'toast.image_added': 'تمت إضافة الصورة بنجاح',
    'toast.variant_added': 'تمت إضافة المتغير',
    'toast.variant_updated': 'تم تحديث المتغير',
    'toast.variant_deleted': 'تم حذف المتغير',
    'toast.review_updated': 'تم تحديث التقييم',
    'toast.package_added': 'تمت إضافة الباقة بنجاح',
    'toast.promo_added': 'تمت إضافة كود الخصم',
    'toast.promo_edited': 'تم تحديث كود الخصم',
    'toast.promo_deleted': 'تم حذف كود الخصم',
    'toast.blog_added': 'تمت إضافة المقال بنجاح',
    'toast.blog_updated': 'تم تحديث المقال',
    'toast.blog_deleted': 'تم حذف المقال',
    'toast.order_paid': 'تم تحديد الطلب كمدفوع',
    'toast.order_delivered': 'تم تحديد الطلب كمُسلَّم',
    'toast.order_deleted': 'تم حذف الطلب',
    'toast.address_added': 'تمت إضافة العنوان بنجاح',
    'toast.status_updated': 'تم تحديث الحالة',
    'toast.user_updated': 'تم تحديث المستخدم بنجاح',
    'toast.user_deleted': 'تم حذف المستخدم',
    'toast.deleted_success': 'تم الحذف بنجاح',
    'toast.error_generic': 'حدث خطأ ما، يرجى المحاولة مرة أخرى',
    'toast.error_reload': 'حدث خطأ ما، يرجى إعادة تحميل الصفحة',
    'toast.login_wrong_creds': 'تحقق من البريد الإلكتروني أو كلمة المرور',
    'toast.passwords_no_match': 'كلمات المرور غير متطابقة',
    'toast.password_change_failed': 'فشل في تغيير كلمة المرور',
    'toast.password_reset_failed': 'فشل في إعادة تعيين كلمة المرور',
    'toast.invalid_reset_link': 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية',
    'toast.user_load_failed': 'فشل في تحميل بيانات المستخدم',
    'toast.user_update_failed': 'فشل في تحديث المستخدم',
    'toast.user_delete_failed': 'فشل في حذف المستخدم',

    // ── Shared Buttons ────────────────────────────────────────────────────────
    'btn.add': 'إضافة',
    'btn.edit': 'تعديل',
    'btn.delete': 'حذف',
    'btn.cancel': 'إلغاء',
    'btn.save': 'حفظ',
    'btn.save_changes': 'حفظ التغييرات',
    'btn.continue': 'متابعة',
    'btn.close': 'إغلاق',
    'btn.retry': 'إعادة المحاولة',
    'btn.back': 'رجوع',
    'btn.back_signin': 'العودة لتسجيل الدخول',
    'btn.sign_in': 'تسجيل الدخول',
    'btn.save_product': 'حفظ المنتج',
    'btn.add_category': 'إضافة فئة',
    'btn.save_category': 'حفظ الفئة',
    'btn.add_product': 'إضافة منتج',
    'btn.add_variant': 'إضافة متغير',
    'btn.save_variant': 'حفظ المتغير',
    'btn.add_blog': 'إضافة مقال',
    'btn.save_blog': 'حفظ المقال',
    'btn.add_package': 'إضافة باقة',
    'btn.save_package': 'حفظ الباقة',
    'btn.add_promo': 'إضافة كود خصم',
    'btn.create_code': 'إنشاء الكود',
    'btn.add_to_package': 'إضافة للباقة',
    'btn.confirm_payment': 'تأكيد الدفع',
    'btn.confirm_delivery': 'تأكيد التسليم',
    'btn.edit_order': 'تعديل الطلب',
    'btn.cancel_order': 'إلغاء الطلب',
    'btn.save_order': 'حفظ الطلب',
    'btn.keep_order': 'الاحتفاظ بالطلب',
    'btn.yes_cancel': 'نعم، إلغاء',
    'btn.delete_user': 'حذف المستخدم',
    'btn.save_address': 'حفظ العنوان',
    'btn.change_address': 'تغيير العنوان',
    'btn.add_address': 'إضافة عنوان جديد',
    'btn.choose_category': 'اختر فئة',
    'btn.choose_product': 'اختر منتج',
    'btn.choose_package': 'اختر باقة',
    'btn.create_admin': 'إنشاء حساب المشرف',
    'btn.update_password': 'تحديث كلمة المرور',
    'btn.send_reset': 'إرسال رابط الإعادة',
    'btn.set_new_password': 'تعيين كلمة المرور الجديدة',
    'btn.request_reset': 'طلب رابط إعادة التعيين',
    'btn.view': 'عرض',

    // ── Shared Labels ─────────────────────────────────────────────────────────
    'lbl.uid': 'المعرف',
    'lbl.client': 'العميل',
    'lbl.date': 'التاريخ',
    'lbl.packages': 'الباقات',
    'lbl.method': 'الطريقة',
    'lbl.paid': 'مدفوع',
    'lbl.delivery': 'التوصيل',
    'lbl.total': 'الإجمالي',
    'lbl.name': 'الاسم',
    'lbl.email': 'البريد الإلكتروني',
    'lbl.email_address': 'عنوان البريد الإلكتروني',
    'lbl.phone': 'الهاتف',
    'lbl.phone_number': 'رقم الهاتف',
    'lbl.username': 'اسم المستخدم',
    'lbl.password': 'كلمة المرور',
    'lbl.current_password': 'كلمة المرور الحالية',
    'lbl.new_password': 'كلمة المرور الجديدة',
    'lbl.confirm_password': 'تأكيد كلمة المرور الجديدة',
    'lbl.price': 'السعر',
    'lbl.quantity': 'الكمية',
    'lbl.discount': 'الخصم',
    'lbl.total_price': 'السعر الإجمالي',
    'lbl.description': 'الوصف',
    'lbl.products': 'المنتجات',
    'lbl.category': 'الفئة',
    'lbl.actions': 'الإجراءات',
    'lbl.content': 'المحتوى',
    'lbl.steps': 'الخطوات',
    'lbl.published': 'تاريخ النشر',
    'lbl.in_stock': 'المخزون',
    'lbl.reason': 'السبب',
    'lbl.status': 'الحالة',
    'lbl.notes': 'الملاحظات',
    'lbl.created': 'تاريخ الإنشاء',
    'lbl.code': 'الكود',
    'lbl.limit': 'الحد الأقصى',
    'lbl.action': 'الإجراء',
    'lbl.image': 'الصورة',
    'lbl.link': 'الرابط',
    'lbl.title': 'العنوان',
    'lbl.rating': 'التقييم',
    'lbl.review': 'المراجعة',
    'lbl.approved': 'معتمد',
    'lbl.visible': 'مرئي',
    'lbl.amount': 'الكمية',
    'lbl.product': 'المنتج',
    'lbl.package': 'الباقة',
    'lbl.full_name': 'الاسم الكامل',
    'lbl.orders': 'الطلبات',
    'lbl.price_modifier': 'معدل السعر (جنيه)',
    'lbl.sku': 'الرمز',
    'lbl.value': 'القيمة',
    'lbl.role': 'الدور',
    'lbl.full_address': 'العنوان الكامل',
    'lbl.city': 'المدينة',
    'lbl.governorate': 'المحافظة',

    // ── Status Values ─────────────────────────────────────────────────────────
    'status.all': 'الكل',
    'status.paid': 'مدفوع',
    'status.unpaid': 'غير مدفوع',
    'status.delivered': 'تم التسليم',
    'status.pending': 'قيد الانتظار',
    'status.undelivered': 'لم يُسلَّم',
    'status.cash': 'نقدي',
    'status.vodafone': 'فودافون',
    'status.yes': 'نعم',
    'status.no': 'لا',
    'status.admin': 'مشرف',
    'status.staff': 'موظف',
    'status.approved': 'معتمد',
    'status.approve': 'اعتماد',
    'status.visible': 'مرئي',
    'status.hidden': 'مخفي',
    'status.cash_on_delivery': 'الدفع عند الاستلام',
    'status.vodafone_cash': 'فودافون كاش',

    // ── Placeholders ──────────────────────────────────────────────────────────
    'ph.search_products': 'ابحث عن منتجات...',
    'ph.search_clients': 'ابحث عن عملاء...',
    'ph.search_users': 'ابحث عن مستخدمين...',
    'ph.search_phone': 'ابحث برقم الهاتف...',
    'ph.search_product': 'ابحث عن منتج...',
    'ph.category_name': 'أدخل اسم الفئة',
    'ph.product_name': 'أدخل اسم المنتج',
    'ph.price': '0.00',
    'ph.quantity': '0',
    'ph.description': 'وصف المنتج...',
    'ph.blog_title': 'أدخل عنوان المقال...',
    'ph.blog_content': 'اكتب المحتوى...',
    'ph.blog_steps': 'أدخل الخطوات...',
    'ph.promo_code': 'مثال: SUMMER20',
    'ph.pack_name': 'اسم الباقة...',
    'ph.notes': 'أضف ملاحظات...',
    'ph.phone_eg': '01XXXXXXXXX',
    'ph.email': 'admin@example.com',
    'ph.password': '••••••••',
    'ph.username_eg': 'مثال: admin_john',
    'ph.full_name': 'الاسم الكامل',
    'ph.phone_admin': '01xxxxxxxxx',

    // ── Page Headers ──────────────────────────────────────────────────────────
    'page.dashboard_eyebrow': 'نظرة عامة',
    'page.dashboard_title': 'الرئيسية',
    'page.dashboard_subtitle': 'مرحباً بعودتك — إليك ما يحدث اليوم.',
    'page.categories_eyebrow': 'الكتالوج',
    'page.categories_title': 'الفئات',
    'page.orders_title': 'جميع الطلبات',
    'page.orders_subtitle': 'عرض وإدارة جميع طلبات العملاء النشطة',
    'page.archived_title': 'الطلبات المؤرشفة',
    'page.archived_subtitle': 'الطلبات المكتملة — مدفوعة ومُسلَّمة',
    'page.cancelled_title': 'الطلبات الملغاة',
    'page.cancelled_subtitle': 'تصفح جميع طلبات العملاء الملغاة',
    'page.order_req_title': 'طلبات الطلب',
    'page.order_req_subtitle': 'طلبات المنتجات المخصصة المقدمة من العملاء',
    'page.clients_eyebrow': 'المستخدمون',
    'page.users_eyebrow': 'الإدارة',
    'page.users_title': 'المستخدمون',
    'page.users_subtitle': 'إدارة حسابات الموظفين وصلاحيات المشرفين.',
    'page.change_pw_title': 'تغيير كلمة المرور',
    'page.change_pw_subtitle': 'تحديث كلمة مرور حساب المشرف',
    'page.blogs_eyebrow': 'المحتوى',
    'page.blogs_title': 'المدونة',
    'page.packs_eyebrow': 'المخزون',
    'page.packs_title': 'الباقات',
    'page.promo_eyebrow': 'التسويق',
    'page.promo_title': 'أكواد الخصم',
    'page.product_eyebrow': 'منتج',
    'page.package_eyebrow': 'باقة',
    'page.orders_count': 'طلب',
    'page.users_count': 'مستخدم',

    // ── KPI / Dashboard ───────────────────────────────────────────────────────
    'kpi.total_clients': 'إجمالي العملاء',
    'kpi.total_revenue': 'الإيرادات الإجمالية',
    'kpi.total_orders': 'إجمالي الطلبات',
    'kpi.pending_orders': 'الطلبات المعلقة',
    'kpi.order_status': 'حالة الطلبات',
    'kpi.total': 'إجمالي',
    'kpi.content_inventory': 'مخزون المحتوى',
    'kpi.no_inventory': 'لا توجد بيانات مخزون بعد',
    'kpi.orders_per_month': 'الطلبات الشهرية',
    'kpi.no_monthly_orders': 'لم يتم تسجيل طلبات في آخر 12 شهرًا',
    'kpi.products': 'المنتجات',
    'kpi.categories': 'الفئات',
    'kpi.packages': 'الباقات',
    'kpi.blogs': 'المقالات',
    'kpi.orders_unit': 'طلب',
    'kpi.last_12_months': 'آخر 12 شهرًا',

    // ── Empty States ──────────────────────────────────────────────────────────
    'empty.categories': 'لا توجد فئات بعد',
    'empty.categories_sub': 'أضف أول فئة للبدء.',
    'empty.orders': 'لا توجد طلبات',
    'empty.orders_sub': 'حاول تعديل عوامل التصفية للعثور على ما تبحث عنه.',
    'empty.archived': 'لا توجد طلبات مؤرشفة',
    'empty.cancelled': 'لا توجد طلبات ملغاة',
    'empty.order_requests': 'لا توجد طلبات',
    'empty.order_requests_sub': 'لا توجد طلبات منتجات مخصصة حاليًا.',
    'empty.delivery_regions': 'لا توجد مناطق توصيل. أضف منطقة أعلاه.',
    'delivery.management': 'الشحن',
    'delivery.title': 'مناطق التوصيل',
    'delivery.add_region': 'إضافة منطقة',
    'delivery.regions': 'كل المناطق',
    'delivery.name_ar': 'الاسم بالعربية',
    'delivery.price': 'السعر (ج)',
    'ph.region_name': 'مثال: القاهرة',
    'empty.clients': 'لا يوجد عملاء',
    'empty.clients_sub': 'لا يوجد عملاء يطابقون بحثك.',
    'empty.users': 'لا يوجد مستخدمون',
    'empty.users_sub': 'لا يوجد مستخدمون يطابقون بحثك.',
    'empty.users_error': 'فشل في تحميل المستخدمين',
    'empty.users_error_sub': 'تحقق من تشغيل الخادم وأنك مسجل دخول كمشرف.',
    'empty.blogs': 'لا توجد مقالات',
    'empty.blogs_sub': 'أنشئ أول مقال للبدء.',
    'empty.blog_products': 'لا توجد منتجات مرتبطة',
    'empty.blog_products_sub': 'لا توجد منتجات مرتبطة بهذا المقال.',
    'empty.packages': 'لا توجد باقات',
    'empty.packages_sub': 'أنشئ أول باقة للبدء.',
    'empty.package_products': 'لا توجد منتجات',
    'empty.package_products_sub': 'هذه الباقة لا تحتوي على منتجات بعد.',
    'empty.promo': 'لا توجد أكواد خصم',
    'empty.promo_sub': 'أنشئ أول كود خصم لتقديم تخفيضات.',
    'empty.promo_orders': 'لا توجد طلبات',
    'empty.promo_orders_sub': 'لم يستخدم أي طلب هذا الكود بعد.',
    'empty.client_orders': 'لا توجد طلبات',
    'empty.client_orders_sub': 'لا توجد طلبات لهذا العميل بعد.',
    'empty.variants': 'لم تتم إضافة متغيرات بعد',
    'empty.reviews': 'لا توجد تقييمات بعد',
    'empty.media': 'لم يتم رفع أي وسائط بعد',
    'empty.no_description': 'لا يوجد وصف',
    'empty.products': 'لا توجد منتجات',
    'empty.products_sub': 'حاول تعديل كلمة البحث.',
    'empty.no_cat_products': 'لا توجد منتجات في هذه الفئة',

    // ── Validation Errors ─────────────────────────────────────────────────────
    'err.required': 'مطلوب',
    'err.email_required': 'البريد الإلكتروني مطلوب',
    'err.email_invalid': 'البريد الإلكتروني غير صالح',
    'err.username_required': 'اسم المستخدم مطلوب',
    'err.name_required': 'الاسم مطلوب',
    'err.phone_required': 'رقم الهاتف مطلوب',
    'err.phone_length': 'يجب أن يكون رقم الهاتف 11 رقمًا بالضبط',
    'err.password_required': 'كلمة المرور مطلوبة',
    'err.password_min': 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
    'err.password_max': 'يجب أن تكون كلمة المرور 128 حرفًا على الأكثر',
    'err.password_mismatch': 'كلمات المرور غير متطابقة.',
    'err.min8': 'الحد الأدنى 8 أحرف مطلوب.',
    'err.phone_valid': 'يرجى إدخال رقم هاتف صالح من 11 رقمًا',
    'err.cat_name_required': 'اسم الفئة مطلوب',
    'err.invalid_reset_link': 'رابط إعادة التعيين غير صالح. يرجى طلب رابط جديد.',

    // ── Auth Pages ────────────────────────────────────────────────────────────
    'auth.brand_sub': 'لوحة تحكم المشرف',
    'auth.admin': 'رضوى خلاف - المشرف',
    'auth.forgot_title': 'نسيت كلمة المرور',
    'auth.forgot_hint': 'أدخل عنوان البريد الإلكتروني لحسابك كمشرف وسنرسل لك رابط إعادة التعيين.',
    'auth.check_inbox': 'تحقق من بريدك الوارد',
    'auth.check_inbox_sub': 'إذا كان هذا البريد مسجلاً، فقد تم إرسال رابط إعادة التعيين. تحقق من مجلد الرسائل غير المرغوب فيها أيضًا.',
    'auth.reset_title': 'إعادة تعيين كلمة المرور',
    'auth.reset_done': 'تمت إعادة تعيين كلمة المرور!',
    'auth.reset_done_sub': 'تم تحديث كلمة مرورك. جاري إعادة التوجيه لتسجيل الدخول…',
    'auth.forgot_pw': 'نسيت كلمة المرور؟',
    'auth.saving': 'جاري الحفظ...',
    'auth.sending': 'جاري الإرسال...',
    'auth.resetting': 'جاري إعادة التعيين...',
    'auth.admin_registration': 'تسجيل مشرف',
    'auth.grant_super': 'منح صلاحيات المشرف الأعلى',
    'auth.super_hint': 'يمكن للمشرفين الأعلى تسجيل مشرفين آخرين وعرض الإيرادات وإدارة جميع المستخدمين.',

    // ── Product Page ──────────────────────────────────────────────────────────
    'prod.details': 'تفاصيل المنتج',
    'prod.media': 'وسائط المنتج',
    'prod.variants': 'المتغيرات',
    'prod.reviews': 'التقييمات',
    'prod.desc_en': 'الوصف (EN)',
    'prod.desc_ar': 'الوصف (AR)',
    'prod.images': 'الصور',
    'prod.screenshots': 'لقطات الشاشة',
    'prod.videos': 'الفيديوهات',
    'prod.name_en': 'الاسم (EN)',
    'prod.name_ar_label': 'الاسم (AR)',
    'prod.add_images': 'إضافة صور رئيسية',
    'prod.add_screenshots': 'إضافة لقطات شاشة',
    'prod.add_video': 'إضافة فيديو',
    'prod.arabic_name': 'الاسم بالعربية',
    'prod.arabic_desc': 'الوصف بالعربية',
    'prod.step1': 'الخطوة 1 من 2',
    'prod.step2': 'الخطوة 2 من 2',
    'prod.step_details': 'التفاصيل',
    'prod.step_media': 'الوسائط',
    'prod.saved_msg': 'تم حفظ المنتج. أضف وسائط أدناه أو تخطَّ للإنهاء.',
    'prod.main_images': 'الصور الرئيسية',
    'prod.product_video': 'فيديو المنتج',
    'prod.edit_title': 'تعديل المنتج',

    // ── Category Page ─────────────────────────────────────────────────────────
    'cat.products_eyebrow': 'فئة',
    'cat.edit_title': 'تعديل الفئة',

    // ── Order Details ─────────────────────────────────────────────────────────
    'ord.information': 'معلومات الطلب',
    'ord.date_time': 'التاريخ والوقت',
    'ord.payment_method': 'طريقة الدفع',
    'ord.delivery_address': 'عنوان التوصيل',
    'ord.mobile': 'رقم الجوال',
    'ord.delivery_fees': 'رسوم التوصيل',
    'ord.no_address': 'لم يتم تحديد عنوان',
    'ord.current_products': 'المنتجات الحالية',
    'ord.current_product': 'المنتج الحالي',
    'ord.remove_tip_pre': 'اضبط الكمية على',
    'ord.remove_tip_post': 'للإزالة',
    'ord.add_products': 'إضافة منتجات',
    'ord.add_packages': 'إضافة باقات',
    'ord.no_address_on_order': 'لا يوجد عنوان في هذا الطلب',
    'ord.contact_phone': 'رقم الهاتف للتواصل',
    'ord.cancel_title': 'إلغاء الطلب؟',
    'ord.cancel_sub': 'لا يمكن التراجع عن هذا الإجراء. سيتم إلغاء الطلب نهائيًا.',
    'ord.included_products': 'المنتجات المضمنة',
    'ord.product_name': 'اسم المنتج',
    'ord.price_unit': 'السعر / الوحدة',
    'ord.subtotal': 'المجموع الفرعي',
    'ord.edit_title': 'تعديل الطلب',

    // ── Blog ──────────────────────────────────────────────────────────────────
    'blog.title_label': 'عنوان المقال',
    'blog.content_label': 'المحتوى',
    'blog.steps_label': 'الخطوات',
    'blog.linked_products': 'المنتجات المرتبطة',
    'blog.choose_product': 'اختر منتجًا',
    'blog.images_label': 'صور المقال',
    'blog.video_label': 'فيديو المقال',
    'blog.edit': 'تعديل المقال',
    'blog.delete': 'حذف المقال',
    'blog.add_images': 'إضافة صور',
    'blog.add_video': 'إضافة فيديو',

    // ── Package ───────────────────────────────────────────────────────────────
    'pack.category': 'الفئة',
    'pack.product_qty': 'كمية المنتج',
    'pack.items': 'عناصر الباقة',
    'pack.pack_name': 'اسم الباقة',
    'pack.image_label': 'صورة الباقة',
    'pack.delete': 'حذف الباقة',
    'pack.edit': 'تعديل الباقة',
    'pack.included': 'المنتجات المضمنة',
    'pack.qty_prefix': 'الكمية:',
    'pack.quantities': 'الكميات',

    // ── Promo ─────────────────────────────────────────────────────────────────
    'promo.code_name': 'اسم كود الخصم',
    'promo.percentage_label': 'نسبة الخصم',
    'promo.limit_label': 'حد الاستخدام',
    'promo.orders_using': 'الطلبات التي استخدمت هذا الكود',
    'promo.add_title': 'إضافة كود خصم',
    'promo.edit_title': 'تعديل كود الخصم',
    'promo.delete_title': 'حذف كود الخصم',

    // ── User / Client ─────────────────────────────────────────────────────────
    'user.edit_info': 'تعديل المعلومات',
    'user.reset_pw': 'إعادة تعيين كلمة المرور',
    'user.admin_priv': 'صلاحيات المشرف',
    'user.admin_hint': 'يمكن للمشرفين الوصول إلى قسم إدارة المستخدمين.',
    'user.delete_confirm': 'حذف المستخدم؟',
    'user.delete_msg': 'لا يمكن التراجع عن هذا الإجراء.',
    'user.min_pw': 'الحد الأدنى 6 أحرف',

    // ── Confirm / Delete ──────────────────────────────────────────────────────
    'confirm.delete_category': 'حذف الفئة',
    'confirm.delete_product': 'حذف المنتج',
    'confirm.delete_blog': 'حذف المقال',
    'confirm.delete_package': 'حذف الباقة',
    'confirm.delete_promo': 'حذف كود الخصم',
    'confirm.cannot_undo': 'لا يمكن التراجع عن هذا الإجراء.',
    'confirm.are_you_sure_delete': 'هل أنت متأكد من حذف',

    // ── Filters ───────────────────────────────────────────────────────────────
    'filter.phone': 'رقم الهاتف',
    'filter.product': 'اسم المنتج',
    'filter.payment': 'حالة الدفع',
    'filter.delivery': 'حالة التوصيل',
  },
};
